import { useEffect, useRef } from 'react';
import { Transformer } from 'markmap-lib';
import { Markmap } from 'markmap-view';
import type { IMarkmapOptions } from 'markmap-common';

interface MindmapViewProps {
  markdown: string;
}

export function MindmapView({ markdown }: MindmapViewProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const markmapRef = useRef<Markmap | null>(null);

  useEffect(() => {
    console.log('MindmapView received markdown:', markdown);
    if (svgRef.current && markdown) {
      try {
        // Clean and validate markdown - handle escaped characters
        let cleanMarkdown = markdown.trim();
        
        // Remove surrounding quotes if present
        if (cleanMarkdown.startsWith('"') && cleanMarkdown.endsWith('"')) {
          cleanMarkdown = cleanMarkdown.slice(1, -1);
        }
        
        // Replace escaped newlines with actual newlines
        cleanMarkdown = cleanMarkdown.replace(/\\n/g, '\n');
        
        if (!cleanMarkdown) {
          console.error('Empty markdown content');
          return;
        }

        // Split into lines and clean each line
        const lines = cleanMarkdown.split('\n').map(line => line.trim()).filter(Boolean);
        
        // Ensure first line is a root heading
        if (!lines[0].startsWith('# ')) {
          lines[0] = `# ${lines[0].replace(/^#+\s*/, '')}`;
        }

        // Join lines back together
        const formattedMarkdown = lines.join('\n');
        console.log('Formatted markdown:', formattedMarkdown);

        // Create transformer and transform markdown
        console.log('Creating transformer...');
        const transformer = new Transformer();
        
        // Transform the markdown
        const { root, features } = transformer.transform(formattedMarkdown);
        
        // Add content to root if it's missing
        if (root && !root.content && formattedMarkdown) {
          const firstLine = formattedMarkdown.split('\n')[0].replace(/^#+ /, '');
          root.content = firstLine;
        }

        console.log('Transformer result:', JSON.stringify({ root, features }, null, 2));

        if (!root || !root.children || root.children.length === 0) {
          console.error('Transformation resulted in empty tree. Root:', root);
          return;
        }

        // Clear existing mindmap
        if (markmapRef.current) {
          console.log('Clearing existing mindmap...');
          markmapRef.current.destroy();
        }

        // Create new mindmap with enhanced options
        console.log('Creating mindmap...');
        const mm = Markmap.create(svgRef.current, {
          autoFit: true,
          duration: 500,
          nodeMinHeight: 24,
          spacingVertical: 16,
          spacingHorizontal: 150,
          paddingX: 32,
          color(node) {
            const colors = ['#2D7FFF', '#0056D2', '#1A3C75'];
            const depth = ((node as any).state?.depth ?? 0) - 1;
            return colors[Math.min(Math.max(0, depth), colors.length - 1)];
          },
          embedGlobalCSS: true,
          fitRatio: 0.9,
          maxWidth: 500,
          initialExpandLevel: -1,
          maxInitialScale: 2,
          pan: true,
          zoom: true,
          style: () => `
            .markmap-node {
              cursor: pointer;
            }
            .markmap-node-circle {
              fill: #fff;
              stroke-width: 1.5px;
            }
            .markmap-node-text {
              fill: currentColor;
              font-size: 16px;
              font-weight: 500;
            }
            .markmap-link {
              fill: none;
              stroke-width: 1.5px;
            }
          `
        }, root);

        markmapRef.current = mm;
        console.log('Mindmap created successfully');
        
        // Fit the mindmap to the viewport after a short delay
        setTimeout(() => {
          mm.fit();
        }, 100);
      } catch (error) {
        console.error('Error creating mindmap:', error);
        if (error instanceof Error) {
          console.error('Error details:', error.message);
          console.error('Error stack:', error.stack);
        }
      }
    }
  }, [markdown]);

  return (
    <div className="w-full h-[600px] relative bg-white dark:bg-gray-800 rounded-lg">
      <svg 
        ref={svgRef} 
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1600 800"
        preserveAspectRatio="xMidYMid meet"
      />
    </div>
  );
} 